import { Briefcase, MapPin, User } from "lucide-react";
import type { Department, Workplace } from "./types";

type Props = {
  name: string;
  email: string;
  workplace: Workplace;
  departmentLabel: string; // resolved name, or the user's own words
  onEdit: (step: 0 | 1 | 2) => void;
};

// Plain-language confirmation. No org-node ids, no role/permission names — just
// "you are X, you work HERE, doing THIS" — what an admin reviews. Each row has
// a Change link that jumps straight back to its step.
function Row({
  icon,
  label,
  value,
  sub,
  onChange,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  onChange: () => void;
}) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-border bg-card px-4 py-3">
      <span className="mt-0.5 text-muted-foreground">{icon}</span>
      <div className="min-w-0 flex-1">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="truncate text-sm font-medium text-foreground">{value}</div>
        {sub && <div className="truncate text-xs text-muted-foreground">{sub}</div>}
      </div>
      <button type="button" onClick={onChange} className="shrink-0 text-xs text-link hover:underline">
        Change
      </button>
    </div>
  );
}

export default function ReviewStep({ name, email, workplace, departmentLabel, onEdit }: Props) {
  const workplaceSub = workplace.isCompany
    ? "Every market and team"
    : workplace.isMarket
    ? "the whole market"
    : [workplace.market, workplace.sublabel].filter(Boolean).join(" › ");

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h2 className="text-base font-semibold text-foreground">Does this look right?</h2>
        <p className="text-sm text-muted-foreground">
          We'll send this to an admin. You'll get a Slack message once they approve.
        </p>
      </div>

      <div className="space-y-2">
        <Row icon={<User size={16} />} label="You are" value={name} sub={email} onChange={() => onEdit(0)} />
        <Row
          icon={<MapPin size={16} />}
          label="You work at"
          value={
            workplace.isCompany ? "Entire company"
            : workplace.isMarket ? `All of ${workplace.label}`
            : workplace.label
          }
          sub={workplaceSub}
          onChange={() => onEdit(1)}
        />
        <Row icon={<Briefcase size={16} />} label="You do" value={departmentLabel} onChange={() => onEdit(2)} />
      </div>
    </div>
  );
}

export type { Department, Workplace };
